import React, { useState, useEffect, useRef } from 'react';
import { useAIModel } from '../contexts/AIModelContext';
import ModelSelector from './ModelSelector';
import axios from 'axios';
import { Calculator, TrendingUp, TrendingDown, RefreshCw, AlertCircle, DollarSign, Calendar, Plus, Trash2, Layers, Brain, Target, Lightbulb, User, Zap, Timer, CandlestickChart, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import TradingChartModal from './TradingChartModal';
import AnalysisChat from './AnalysisChat';
import PositionAnalysisResults from './PositionAnalysisResults';
import { normalizeSymbol } from '../utils/symbolUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Strategy templates
const STRATEGY_TEMPLATES = [
  { id: 'single', name: 'Single Leg', legs: 1 },
  { id: 'vertical_spread', name: 'Vertical Spread', legs: 2 },
  { id: 'iron_condor', name: 'Iron Condor', legs: 4 },
  { id: 'straddle', name: 'Straddle/Strangle', legs: 2 },
  { id: 'butterfly', name: 'Butterfly', legs: 3 },
  { id: 'custom', name: 'Custom Multi-Leg', legs: 0 },
];

const createEmptyLeg = () => ({
  option_type: 'call',
  position_type: 'long',
  strike: '',
  quantity: 1,
  entry_premium: '',
  current_premium: ''
});

const ManualPositionAnalyzer = ({ initialPosition }) => {
  // Common position info
  const [symbol, setSymbol] = useState('');
  const [expiration, setExpiration] = useState('');
  const [strategyType, setStrategyType] = useState('single');
  
  // Multi-leg support
  const [legs, setLegs] = useState([createEmptyLeg()]);
  
  // Analysis settings
  const { model: selectedModel, setModel: setSelectedModel } = useAIModel();
  const [riskTolerance, setRiskTolerance] = useState('moderate');
  const [investmentGoal, setInvestmentGoal] = useState('growth');
  
  // State
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [fetchingPremiums, setFetchingPremiums] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(null);
  
  // Chart modal state
  const [isChartOpen, setIsChartOpen] = useState(false);
  
  // Chat state
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  // Auto-refresh state
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30);
  const [lastRefresh, setLastRefresh] = useState(null);
  const autoRefreshRef = useRef(null);
  
  // Track last processed timestamp to avoid duplicate processing
  const lastProcessedTimestamp = useRef(null);
  
  // Reset the ref when component mounts with new key (which happens when initialPosition changes)
  useEffect(() => {
    // Reset on mount
    lastProcessedTimestamp.current = null;
  }, []); // Empty deps - runs only on mount

  // Load initial position from portfolio - use timestamp to detect changes
  useEffect(() => {
    // Check if we have a new position to process
    const timestamp = initialPosition?._actionTimestamp;
    
    if (!initialPosition || !timestamp) {
      console.log('ManualPositionAnalyzer: No initialPosition or no timestamp');
      return;
    }
    
    // Skip if we already processed this timestamp
    if (lastProcessedTimestamp.current === timestamp) {
      console.log('ManualPositionAnalyzer: Already processed timestamp:', timestamp);
      return;
    }
    
    console.log('ManualPositionAnalyzer: Processing new position with timestamp:', timestamp);
    console.log('ManualPositionAnalyzer: initialPosition:', JSON.stringify(initialPosition, null, 2));
    
    // Mark as processed
    lastProcessedTimestamp.current = timestamp;
    
    // Check if this is a spread (multiple legs) or single position
    const isSpread = initialPosition.is_spread || initialPosition.legs?.length > 1;
    
    console.log('Is spread:', isSpread, 'Has legs:', !!initialPosition.legs);
    
    if (isSpread && initialPosition.legs) {
      // Handle spread/multi-leg position
      const newSymbol = initialPosition.underlying_symbol || '';
      console.log('Setting symbol to:', newSymbol);
      setSymbol(newSymbol);
      
      // Map the strategy type to our template IDs
      const spreadType = initialPosition.strategy_type;
      let templateType = 'custom'; // Default to custom for unknown spreads
      
      if (spreadType === 'vertical_spread') {
        templateType = 'vertical_spread';
      } else if (spreadType === 'iron_condor') {
        templateType = 'iron_condor';
      } else if (spreadType === 'straddle' || spreadType === 'strangle') {
        templateType = 'straddle';
      } else if (spreadType === 'butterfly') {
        templateType = 'butterfly';
      } else if (initialPosition.legs.length === 1) {
        templateType = 'single';
      }
      
      console.log('Setting strategyType to:', templateType);
      setStrategyType(templateType);
      
      // Use expiration from spread or first leg
      const expDate = initialPosition.expiration_date || initialPosition.legs[0]?.expiration_date;
      if (expDate) {
        // Handle different date formats
        const dateStr = typeof expDate === 'string' ? expDate.split('T')[0] : new Date(expDate).toISOString().split('T')[0];
        console.log('Setting expiration to:', dateStr);
        setExpiration(dateStr);
      }
      
      // Convert legs to form format
      const newLegs = initialPosition.legs.map((leg, idx) => {
        console.log(`Processing leg ${idx}:`, leg);
        const formLeg = {
          option_type: leg.option_type || 'call',
          position_type: leg.quantity > 0 ? 'long' : 'short',
          strike: leg.strike_price?.toString() || leg.strike?.toString() || '',
          quantity: Math.abs(leg.quantity) || 1,
          entry_premium: leg.average_open_price?.toString() || leg.entry_premium?.toString() || '',
          current_premium: leg.close_price?.toString() || leg.current_premium?.toString() || ''
        };
        console.log(`Form leg ${idx}:`, formLeg);
        return formLeg;
      });
      
      console.log('Setting legs to:', newLegs);
      setLegs(newLegs);
      setCurrentPrice(initialPosition.legs[0]?.close_price || initialPosition.legs[0]?.current_premium);
      
      // Clear previous analysis
      setAnalysis(null);
      setIsChatOpen(false);
      
    } else if (initialPosition.is_option) {
      // Handle single-leg option position
      console.log('Handling single-leg option');
      setSymbol(initialPosition.underlying_symbol || '');
      setStrategyType('single');
      
      if (initialPosition.expiration_date) {
        const expDate = initialPosition.expiration_date;
        const dateStr = typeof expDate === 'string' ? expDate.split('T')[0] : new Date(expDate).toISOString().split('T')[0];
        setExpiration(dateStr);
      }
      
      const newLeg = {
        option_type: initialPosition.option_type || 'call',
        position_type: initialPosition.quantity > 0 ? 'long' : 'short',
        strike: initialPosition.strike_price?.toString() || '',
        quantity: Math.abs(initialPosition.quantity) || 1,
        entry_premium: initialPosition.average_open_price?.toString() || initialPosition.entry_premium?.toString() || '',
        current_premium: initialPosition.close_price?.toString() || initialPosition.current_premium?.toString() || ''
      };
      console.log('Setting single leg:', newLeg);
      setLegs([newLeg]);
      setCurrentPrice(initialPosition.close_price || initialPosition.current_premium);
      
      // Clear previous analysis
      setAnalysis(null);
      setIsChatOpen(false);
    } else {
      console.log('Position type not recognized - is_spread:', initialPosition.is_spread, 'is_option:', initialPosition.is_option);
    }
  }, [initialPosition]);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh && symbol && expiration && legs.some(l => l.strike)) {
      fetchCurrentPremiums(true);
      
      autoRefreshRef.current = setInterval(() => {
        fetchCurrentPremiums(true);
      }, refreshInterval * 1000);
      
      return () => {
        if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
      };
    } else {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    }
  }, [autoRefresh, refreshInterval, symbol, expiration]);

  // Fast premium fetching function
  const fetchCurrentPremiums = async (silent = false) => {
    // Normalize symbol before API call
    const normalizedSym = normalizeSymbol(symbol);
    if (!normalizedSym || !expiration) {
      if (!silent) toast.error('Please enter symbol and expiration first');
      return;
    }
    
    const legsWithStrikes = legs.filter(leg => leg.strike);
    if (legsWithStrikes.length === 0) {
      if (!silent) toast.error('Please enter at least one strike price');
      return;
    }
    
    if (!silent) setFetchingPremiums(true);
    if (!silent) toast.info('Fetching current premiums...');
    
    try {
      const response = await axios.post(`${API}/data/premiums`, {
        symbol: normalizedSym,
        expiration: expiration,
        legs: legsWithStrikes.map(leg => ({
          strike: parseFloat(leg.strike),
          option_type: leg.option_type
        }))
      }, { timeout: 15000 });
      
      // Create a map of fetched premiums
      const premiumMap = {};
      response.data.legs.forEach(leg => {
        if (leg.premium) {
          premiumMap[`${leg.strike}_${leg.option_type}`] = leg.premium;
        }
      });
      
      // Update legs with fetched current premiums
      const updatedLegs = legs.map(leg => {
        const key = `${leg.strike}_${leg.option_type}`;
        if (premiumMap[key]) {
          return { ...leg, current_premium: premiumMap[key].toFixed(2) };
        }
        return leg;
      });
      
      setLegs(updatedLegs);
      setCurrentPrice(response.data.current_price);
      setLastRefresh(new Date());
      
      const found = response.data.legs.filter(l => l.found).length;
      if (!silent) toast.success(`Fetched ${found}/${legsWithStrikes.length} premiums. Stock: $${response.data.current_price}`);
    } catch (error) {
      console.error('Error fetching premiums:', error);
      if (!silent) toast.error('Failed to fetch premiums. Please enter manually.');
    } finally {
      if (!silent) setFetchingPremiums(false);
    }
  };

  // Add a new leg
  const addLeg = () => {
    if (legs.length < 6) {
      setLegs([...legs, createEmptyLeg()]);
    } else {
      toast.error('Maximum 6 legs allowed');
    }
  };

  // Remove a leg
  const removeLeg = (index) => {
    if (legs.length > 1) {
      setLegs(legs.filter((_, i) => i !== index));
    }
  };

  // Update a specific leg
  const updateLeg = (index, field, value) => {
    const newLegs = [...legs];
    newLegs[index] = { ...newLegs[index], [field]: value };
    setLegs(newLegs);
  };

  // Apply strategy template
  const applyTemplate = (templateId) => {
    setStrategyType(templateId);
    switch (templateId) {
      case 'single':
        setLegs([createEmptyLeg()]);
        break;
      case 'vertical_spread':
        setLegs([
          { option_type: 'call', position_type: 'long', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
          { option_type: 'call', position_type: 'short', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
        ]);
        break;
      case 'iron_condor':
        setLegs([
          { option_type: 'put', position_type: 'long', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
          { option_type: 'put', position_type: 'short', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
          { option_type: 'call', position_type: 'short', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
          { option_type: 'call', position_type: 'long', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
        ]);
        break;
      case 'straddle':
        setLegs([
          { option_type: 'call', position_type: 'long', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
          { option_type: 'put', position_type: 'long', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
        ]);
        break;
      case 'butterfly':
        setLegs([
          { option_type: 'call', position_type: 'long', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
          { option_type: 'call', position_type: 'short', strike: '', quantity: 2, entry_premium: '', current_premium: '' },
          { option_type: 'call', position_type: 'long', strike: '', quantity: 1, entry_premium: '', current_premium: '' },
        ]);
        break;
      case 'custom':
        break;
      default:
        setLegs([createEmptyLeg()]);
    }
  };

  const getStrategyDescription = () => {
    const longLegs = legs.filter(l => l.position_type === 'long').length;
    const shortLegs = legs.filter(l => l.position_type === 'short').length;
    const calls = legs.filter(l => l.option_type === 'call').length;
    const puts = legs.filter(l => l.option_type === 'put').length;
    
    return `${legs.length} leg${legs.length > 1 ? 's' : ''}: ${longLegs} long, ${shortLegs} short | ${calls} call${calls !== 1 ? 's' : ''}, ${puts} put${puts !== 1 ? 's' : ''}`;
  };

  const analyzePosition = async () => {
    // Normalize symbol before validation
    const normalizedSym = normalizeSymbol(symbol);
    
    // Validation
    if (!normalizedSym) {
      toast.error('Please enter a valid stock symbol');
      return;
    }
    if (!expiration) {
      toast.error('Please select an expiration date');
      return;
    }
    
    // Validate legs
    for (let i = 0; i < legs.length; i++) {
      const leg = legs[i];
      if (!leg.strike || !leg.entry_premium) {
        toast.error(`Please fill in strike and entry premium for Leg ${i + 1}`);
        return;
      }
    }

    setAnalyzing(true);
    setAnalysis(null);
    setAnalysisError(null);
    toast.info('Analyzing your position... This may take 15-30 seconds.');
    
    try {
      const response = await axios.post(`${API}/analyze/multi-leg-position`, {
        symbol: normalizedSym,
        expiration,
        legs: legs.map(leg => ({
          option_type: leg.option_type,
          position_type: leg.position_type,
          strike: parseFloat(leg.strike),
          quantity: parseInt(leg.quantity) || 1,
          entry_premium: parseFloat(leg.entry_premium),
          current_premium: leg.current_premium ? parseFloat(leg.current_premium) : null,
          multiplier: 100
        })),
        strategy_type: strategyType,
        risk_tolerance: riskTolerance,
        investment_goal: investmentGoal,
        ai_model: selectedModel
      }, { timeout: 55000 });
      
      setAnalysis(response.data);
      toast.success('Position analysis complete!');
    } catch (error) {
      console.error('Error analyzing position:', error);
      const msg = (error.code === 'ECONNABORTED' || error.message?.includes('timeout'))
        ? 'Analysis timed out. Please try again — the AI model may be busy.'
        : (typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Failed to analyze position. Please try again.');
      setAnalysisError(msg);
      toast.error(msg, { duration: 8000 });
    } finally {
      setAnalyzing(false);
    }
  };

  const resetForm = () => {
    setSymbol('');
    setExpiration('');
    setStrategyType('single');
    setLegs([createEmptyLeg()]);
    setAnalysis(null);
    setIsChatOpen(false);
  };

  return (
    <div className="space-y-6" data-testid="manual-position-analyzer">
      {/* Floating Chat Button - Always visible when analysis exists */}
      {analysis && !isChatOpen && (
        <Button
          onClick={() => setIsChatOpen(true)}
          className="fixed bottom-6 right-6 z-30 h-14 w-14 rounded-full shadow-lg bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 p-0"
          data-testid="floating-chat-btn"
          title="Chat about this analysis"
        >
          <MessageCircle className="h-6 w-6" />
        </Button>
      )}

      {/* Header */}
      <Card className="glass-effect border-0 shadow-lg bg-gradient-to-r from-indigo-50 to-purple-50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
                <Calculator className="h-8 w-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl">Position Analyzer</CardTitle>
                <CardDescription className="text-base">
                  Analyze single or multi-leg option positions with AI-powered "If You Were Me" recommendations
                </CardDescription>
              </div>
            </div>
            {/* Auto-refresh toggle */}
            <div className="flex items-center space-x-4 bg-white/50 rounded-lg px-4 py-2">
              <div className="flex items-center space-x-2">
                <Timer className={`h-4 w-4 ${autoRefresh ? 'text-green-600 animate-pulse' : 'text-gray-400'}`} />
                <Label htmlFor="auto-refresh-pa" className="text-sm font-medium cursor-pointer">
                  Auto-refresh
                </Label>
                <Switch
                  id="auto-refresh-pa"
                  checked={autoRefresh}
                  onCheckedChange={setAutoRefresh}
                  data-testid="auto-refresh-toggle-position"
                />
              </div>
              {autoRefresh && (
                <Select value={String(refreshInterval)} onValueChange={(v) => setRefreshInterval(Number(v))}>
                  <SelectTrigger className="w-20 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15s</SelectItem>
                    <SelectItem value="30">30s</SelectItem>
                    <SelectItem value="60">1m</SelectItem>
                    <SelectItem value="120">2m</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {autoRefresh && lastRefresh && (
                <span className="text-xs text-gray-500">
                  Last: {lastRefresh.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Input Form */}
      <Card className="glass-effect border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Target className="h-5 w-5 mr-2 text-indigo-600" />
            Your Current Position
          </CardTitle>
          <CardDescription>Enter your option position details (single or multi-leg)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Row 1: Symbol, Expiration, Strategy Type */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Stock Symbol *</Label>
              <div className="flex space-x-2">
                <Input
                  placeholder="e.g., AAPL"
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                  className="text-lg font-semibold"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => symbol && setIsChartOpen(true)}
                  disabled={!symbol}
                  className="px-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0"
                  title="View Live Chart"
                  data-testid="view-chart-btn"
                >
                  <CandlestickChart className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label>Expiration Date *</Label>
              <Input
                type="date"
                value={expiration}
                onChange={(e) => setExpiration(e.target.value)}
              />
            </div>
            <div>
              <Label>Strategy Template</Label>
              <Select value={strategyType} onValueChange={applyTemplate}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STRATEGY_TEMPLATES.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      <span className="flex items-center">
                        <Layers className="h-4 w-4 mr-2 text-indigo-600" />
                        {template.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Legs Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold flex items-center">
                <Layers className="h-4 w-4 mr-2 text-indigo-600" />
                Position Legs ({legs.length})
                {currentPrice && (
                  <Badge className="ml-3 bg-indigo-100 text-indigo-700">${currentPrice}</Badge>
                )}
              </Label>
              <div className="flex space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fetchCurrentPremiums(false)}
                  disabled={fetchingPremiums || !symbol || !expiration}
                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                  data-testid="fetch-premiums-btn"
                >
                  {fetchingPremiums ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
                  Fetch Premiums
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addLeg}
                  disabled={legs.length >= 6}
                  className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Leg
                </Button>
              </div>
            </div>

            {/* Strategy Summary */}
            <div className="bg-slate-50 rounded-lg px-4 py-2 text-sm text-gray-600">
              {getStrategyDescription()}
            </div>

            {/* Legs Grid */}
            <div className="space-y-3">
              {legs.map((leg, index) => (
                <div key={index} className="bg-gradient-to-r from-slate-50 to-indigo-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-indigo-700">Leg {index + 1}</span>
                    {legs.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLeg(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                    <div>
                      <Label className="text-xs">Type</Label>
                      <Select 
                        value={leg.option_type} 
                        onValueChange={(v) => updateLeg(index, 'option_type', v)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="call">
                            <span className="flex items-center">
                              <TrendingUp className="h-3 w-3 mr-1 text-green-600" /> Call
                            </span>
                          </SelectItem>
                          <SelectItem value="put">
                            <span className="flex items-center">
                              <TrendingDown className="h-3 w-3 mr-1 text-red-600" /> Put
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Position</Label>
                      <Select 
                        value={leg.position_type} 
                        onValueChange={(v) => updateLeg(index, 'position_type', v)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="long">Long (Buy)</SelectItem>
                          <SelectItem value="short">Short (Sell)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Strike *</Label>
                      <Input
                        type="number"
                        step="0.5"
                        placeholder="150"
                        value={leg.strike}
                        onChange={(e) => updateLeg(index, 'strike', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Qty</Label>
                      <Input
                        type="number"
                        min="1"
                        value={leg.quantity}
                        onChange={(e) => updateLeg(index, 'quantity', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Entry Premium *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="2.50"
                        value={leg.entry_premium}
                        onChange={(e) => updateLeg(index, 'entry_premium', e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Current (opt)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Auto"
                        value={leg.current_premium}
                        onChange={(e) => updateLeg(index, 'current_premium', e.target.value)}
                        className="h-9"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Personal Profile for "If You Were Me" */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
            <h3 className="text-sm font-semibold text-amber-800 mb-3 flex items-center">
              <User className="h-4 w-4 mr-2" />
              Your Profile (for "If You Were Me" Analysis)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-amber-700">Risk Tolerance</Label>
                <RadioGroup value={riskTolerance} onValueChange={setRiskTolerance} className="flex space-x-4 mt-1">
                  <div className="flex items-center space-x-1">
                    <RadioGroupItem value="conservative" id="risk-conservative" />
                    <label htmlFor="risk-conservative" className="text-xs cursor-pointer">Conservative</label>
                  </div>
                  <div className="flex items-center space-x-1">
                    <RadioGroupItem value="moderate" id="risk-moderate" />
                    <label htmlFor="risk-moderate" className="text-xs cursor-pointer">Moderate</label>
                  </div>
                  <div className="flex items-center space-x-1">
                    <RadioGroupItem value="aggressive" id="risk-aggressive" />
                    <label htmlFor="risk-aggressive" className="text-xs cursor-pointer">Aggressive</label>
                  </div>
                </RadioGroup>
              </div>
              <div>
                <Label className="text-xs text-amber-700">Investment Goal</Label>
                <RadioGroup value={investmentGoal} onValueChange={setInvestmentGoal} className="flex space-x-4 mt-1">
                  <div className="flex items-center space-x-1">
                    <RadioGroupItem value="income" id="goal-income" />
                    <label htmlFor="goal-income" className="text-xs cursor-pointer">Income</label>
                  </div>
                  <div className="flex items-center space-x-1">
                    <RadioGroupItem value="growth" id="goal-growth" />
                    <label htmlFor="goal-growth" className="text-xs cursor-pointer">Growth</label>
                  </div>
                  <div className="flex items-center space-x-1">
                    <RadioGroupItem value="hedge" id="goal-hedge" />
                    <label htmlFor="goal-hedge" className="text-xs cursor-pointer">Hedge</label>
                  </div>
                </RadioGroup>
              </div>
            </div>
          </div>

          {/* AI Model and Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Brain className="h-5 w-5 text-indigo-600" />
              <ModelSelector />
            </div>
            
            <div className="flex items-center space-x-3">
              <Button variant="outline" onClick={resetForm} disabled={analyzing}>
                Reset
              </Button>
              <Button
                onClick={analyzePosition}
                disabled={analyzing}
                className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 px-8"
              >
                {analyzing ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Calculator className="h-4 w-4 mr-2" />
                    Analyze Position
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {analysisError && !analyzing && (
        <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-sm text-red-800" data-testid="position-analysis-error">
          <p className="font-semibold">Analysis could not be completed</p>
          <p className="mt-1">{analysisError}</p>
        </div>
      )}

      {/* Analysis Results — New structured display */}
      {analysis && (
        <div className="space-y-6 fade-in">
          <PositionAnalysisResults analysis={analysis} />

          {/* Chat Button */}
          <Card className="glass-effect border-0 shadow-lg bg-gradient-to-r from-amber-50 to-orange-50">
            <CardContent className="pt-4">
              <Button
                onClick={() => setIsChatOpen(true)}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                data-testid="open-chat-btn"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Chat with AI About This Analysis
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Trading Chart Modal */}
      <TradingChartModal
        isOpen={isChartOpen}
        onClose={() => setIsChartOpen(false)}
        symbol={symbol}
      />

      {/* Analysis Chat */}
      <AnalysisChat
        analysis={analysis}
        symbol={symbol}
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
      />
    </div>
  );
};

export default ManualPositionAnalyzer;
